import React, { useEffect, useState } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Provider as PaperProvider } from 'react-native-paper';
import { AppState, AppStateStatus } from 'react-native';
import messaging from '@react-native-firebase/messaging';

import AppNavigator from './src/navigation/AppNavigator';
import ErrorBoundary from './src/components/ErrorBoundary';
import ForceUpdateModal from './src/components/ForceUpdateModal';
import errorLoggingService from './src/services/errorLoggingService';
import updateCheckerService from './src/services/updateCheckerService';
import { sessionManager } from './src/services/sessionManager';
import { UpdateInfo } from './src/services/updateCheckerService';
import { pushNotificationService } from './src/services/pushNotificationService';
import { navigateToNotificationTarget } from './src/utils/notificationNavigator';
import { SubscriptionProvider } from './src/contexts/SubscriptionContext';
import { NotificationProvider, triggerNotificationRefresh } from './src/contexts/NotificationContext';
import { UserSelectionProvider } from './src/contexts/UserSelectionContext';
import { AIProvider } from './src/contexts/AIContext';
import { AIAssistantButton } from './src/components/ai/AIAssistantButton';
import { AIChat } from './src/components/ai/AIChat';
import { analytics, AnalyticsEvent } from './src/services/analytics';
import { routeToMigrationIfNeeded } from './src/services/migration/migrationRouter';
import { clearCachedSalt } from './src/services/encryption/UserSalt';
import {
  markBackgrounded,
  maybeLockOnForeground,
} from './src/services/auth/idleLock';
import { getIdleLockThreshold } from './src/services/auth/idleLockPreferences';
import { getAuthToken } from './src/services/auth/tokenStorage';
import IdleLockOverlay from './src/components/IdleLockOverlay';
import { wrapRootComponent } from './src/services/sentry/sentry';

function App(): React.JSX.Element {
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);

  useEffect(() => {
    // Setup global error handlers on app mount
    errorLoggingService.setupGlobalErrorHandlers();

    // Initialize session manager for token refresh
    sessionManager.initialize();

    // Initialize product analytics (no-op if POSTHOG_API_KEY missing)
    analytics.init().then(() => analytics.track(AnalyticsEvent.AppOpened));

    // If a logged-in user still has v1-format data, route them to the
    // MigrationScreen before any other flow. No-op for new installs and for
    // users who have already completed migration.
    routeToMigrationIfNeeded();

    // Check for updates on app launch
    checkForUpdates();

    // Check for updates when app comes to foreground
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // Set up push notifications
  useEffect(() => {
    const setupPushNotifications = async () => {
      try {
        const hasPermission = await pushNotificationService.requestPermission();
        if (hasPermission) {
          await pushNotificationService.getAndRegisterToken();
        }
      } catch (error) {
        console.log('[Push] Setup error (non-fatal):', error);
      }
    };
    setupPushNotifications();

    // Foreground message handler - triggers notification count refresh
    const unsubscribeForeground = pushNotificationService.setupForegroundHandler(() => {
      triggerNotificationRefresh();
    });

    // Token refresh handler
    const unsubscribeTokenRefresh = pushNotificationService.setupTokenRefreshListener();

    // Handle notification tap when app is in background
    const unsubscribeOnOpen = messaging().onNotificationOpenedApp(remoteMessage => {
      try {
        if (remoteMessage.data) {
          const actionData = remoteMessage.data.actionData ? JSON.parse(remoteMessage.data.actionData as string) : {};
          navigateToNotificationTarget(remoteMessage.data.actionType as string, actionData);
        }
      } catch (error) {
        console.log('[Push] Error handling notification open:', error);
      }
    });

    // Handle notification tap when app was killed
    messaging().getInitialNotification().then(remoteMessage => {
      try {
        if (remoteMessage?.data) {
          const actionData = remoteMessage.data.actionData ? JSON.parse(remoteMessage.data.actionData as string) : {};
          // Small delay to let navigation mount
          setTimeout(() => {
            navigateToNotificationTarget(remoteMessage.data!.actionType as string, actionData);
          }, 1000);
        }
      } catch (error) {
        console.log('[Push] Error handling initial notification:', error);
      }
    }).catch(error => {
      console.log('[Push] Error getting initial notification:', error);
    });

    return () => {
      unsubscribeForeground();
      unsubscribeTokenRefresh();
      unsubscribeOnOpen();
    };
  }, []);

  const handleAppStateChange = async (nextAppState: AppStateStatus) => {
    if (nextAppState === 'active') {
      // App came to foreground, refresh token if needed and check for updates
      sessionManager.initialize();
      checkForUpdates();

      // Idle-lock check (SECURITY_AUDIT §3.4). Only meaningful for
      // authenticated users — pre-login screens don't need a re-auth gate
      // because they're already the auth gate.
      try {
        const token = await getAuthToken();
        if (token) {
          const threshold = await getIdleLockThreshold();
          maybeLockOnForeground(threshold);
        }
      } catch (err) {
        console.log('[IdleLock] foreground check failed (non-fatal):', err);
      }
      return;
    }
    if (nextAppState === 'background' || nextAppState === 'inactive') {
      // Start the idle clock the moment we leave the foreground.
      markBackgrounded();
      // Drop the in-memory PBKDF2 salt cache so a subsequent decrypt on
      // resume has to re-read the keychain (which re-prompts biometry if
      // the user's access-control policy requires it). JS doesn't expose
      // memset, but nulling the reference lets GC reclaim the buffer.
      clearCachedSalt();
    }
  };

  const checkForUpdates = async () => {
    try {
      // Check if update check is due
      const shouldCheck = await updateCheckerService.shouldCheckForUpdates();

      if (shouldCheck) {
        const update = await updateCheckerService.getUpdateStatus();

        if (update.available && update.forceUpdate) {
          setUpdateInfo(update);
          // Not recorded as checked: a forced update must come back on the
          // next launch or return to the app. Recording it let a restart hide
          // the "Update Required" screen for a day (fixed 2026-10-23).
          return;
        } else if (update.available) {
          // Non-critical update, show alert
          await updateCheckerService.checkAndNotify(false);
        }

        await updateCheckerService.recordUpdateCheck();
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  };

  return (
    <ErrorBoundary>
      <SafeAreaProvider>
        <NotificationProvider>
          <SubscriptionProvider>
            <UserSelectionProvider>
              <AIProvider>
                <PaperProvider>
                  <AppNavigator />
                  {/* AI Assistant Components */}
                  <AIAssistantButton />
                  <AIChat />
                  {/* Idle-lock overlay — renders on top of everything when
                      the app has been in background longer than the user's
                      configured threshold (SECURITY_AUDIT §3.4). */}
                  <IdleLockOverlay />
                  {updateInfo && updateInfo.forceUpdate && (
                    <ForceUpdateModal
                      visible={true}
                      version={updateInfo.latestVersion}
                      releaseNotes={updateInfo.releaseNotes}
                      updateUrl={updateInfo.updateUrl}
                    />
                  )}
                </PaperProvider>
              </AIProvider>
            </UserSelectionProvider>
          </SubscriptionProvider>
        </NotificationProvider>
      </SafeAreaProvider>
    </ErrorBoundary>
  );
}

// Wrap so Sentry captures React render errors. No-op if Sentry init
// didn't run (missing DSN) — returns App unchanged.
export default wrapRootComponent(App);
